import {
  FinancialSettings,
  FinancialSettingsPixKeyType,
  FinancialSettingsPixKeyTypeLabels,
} from "@/models/financialSettings.model";
import { Corban, MasterUser } from "@/models/corban.model";
import { EditIcon } from "@chakra-ui/icons";
import {
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Flex,
  Heading,
  HStack,
  Icon,
  SimpleGrid,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import { IconType } from "react-icons";
import {
  FaBuilding,
  FaHistory,
  FaUniversity,
  FaUserTie,
} from "react-icons/fa";
import { documentFormatter } from "@/utils/documentFormatter";
import { cpfFormatter } from "@/utils/cpfFormatter";
import { formatPhone } from "@/utils/formatPhone";
import { useNavigate } from "react-router-dom";
import {
  SplitStatusColors,
  SplitStatusEnum,
  SplitStatusLabels,
} from "@/models/splitStatusEnum.model";
import SplitTimeline from "../SplitTimeline/SplitTimeline";

interface AnalizeSplitApproveDataProps {
  financialSettings?: FinancialSettings;
  corban?: Corban;
  masterUser?: MasterUser | null;
  splitStatus?: SplitStatusEnum | null;
}

const EMPTY_VALUE = "—";

/** Bloco rótulo + valor usado em todas as seções. */
function InfoField({
  label,
  value,
  highlight,
  children,
}: {
  label: string;
  value?: string | number | null;
  highlight?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <VStack align="start" spacing={1}>
      <Text
        fontSize="11px"
        fontWeight="semibold"
        textTransform="uppercase"
        letterSpacing="0.06em"
        color="gray.500"
      >
        {label}
      </Text>
      {children ?? (
        <Text
          fontSize="sm"
          fontWeight={highlight ? "bold" : "medium"}
          color={highlight ? "blue.600" : "gray.800"}
          wordBreak="break-word"
        >
          {value === null || value === undefined || value === ""
            ? EMPTY_VALUE
            : value}
        </Text>
      )}
    </VStack>
  );
}

/** Card de seção com cabeçalho iconizado. */
function SectionCard({
  title,
  icon,
  accent,
  action,
  children,
}: {
  title: string;
  icon: IconType;
  accent: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card
      borderRadius="xl"
      borderWidth="1px"
      borderColor="gray.200"
      boxShadow="sm"
      overflow="hidden"
    >
      <CardHeader px={5} py={4}>
        <HStack justify="space-between" align="center" spacing={4}>
          <HStack spacing={3}>
            <Flex
              align="center"
              justify="center"
              boxSize={9}
              borderRadius="lg"
              bg={`${accent}.50`}
              color={`${accent}.500`}
              flexShrink={0}
            >
              <Icon as={icon} boxSize={4} />
            </Flex>
            <Heading size="sm" color="gray.800">
              {title}
            </Heading>
          </HStack>
          {action}
        </HStack>
      </CardHeader>
      <Divider borderColor="gray.100" />
      <CardBody px={5} py={5}>
        {children}
      </CardBody>
    </Card>
  );
}

export default function AnalizeSplitApproveData({
  financialSettings,
  corban,
  masterUser,
  splitStatus,
}: AnalizeSplitApproveDataProps) {
  const navigate = useNavigate();

  const handleEditCorban = () => {
    if (corban?.id) {
      navigate(`/corban/editar/${corban.id}`);
    }
  };
  const getCommissionModeDescription = () => {
    if (financialSettings?.CommissionMode?.description) {
      return financialSettings.CommissionMode.description;
    }

    return "Não definido";
  };

  const objectTimeline =
    corban?.timeline || corban?.splitSolicitation?.timeline;


  const isBankAccount =
    financialSettings?.pixKeyType === FinancialSettingsPixKeyType.BANK_ACCOUNT;

  const taxes = financialSettings?.FinancialSettingsTax ?? [];

  const initials = (corban?.businessName || corban?.legalName || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

  const statusScheme = splitStatus ? SplitStatusColors[splitStatus] : "gray";

  // Responsável pela operação é o master da promotora, que solicita o split
  // para o corban abaixo dela — não o master do corban.
  const promoterMaster = corban?.promoterMasterUser;
  const promoterMasterName = promoterMaster
    ? `${promoterMaster.masterUserName ?? ""} ${
        promoterMaster.masterUserLastName ?? ""
      }`.trim()
    : "";

  return (
    <VStack spacing={5} align="stretch" w="100%" overflow="visible">
      {/* Resumo do corban */}
      <Box
        borderRadius="xl"
        borderWidth="1px"
        borderColor="gray.200"
        bg="white"
        boxShadow="sm"
        px={5}
        py={4}
      >
        <Flex
          align={{ base: "start", md: "center" }}
          justify="space-between"
          gap={4}
          direction={{ base: "column", md: "row" }}
        >
          <HStack spacing={4} align="center">
            <Flex
              align="center"
              justify="center"
              boxSize={12}
              borderRadius="xl"
              bg="blue.500"
              color="white"
              fontWeight="bold"
              fontSize="md"
              flexShrink={0}
            >
              {initials}
            </Flex>
            <VStack align="start" spacing={1}>
              <Heading size="md" color="gray.800">
                {corban?.businessName || corban?.legalName || EMPTY_VALUE}
              </Heading>
              <HStack spacing={3} flexWrap="wrap">
                <Text fontSize="sm" color="gray.500">
                  CNPJ {documentFormatter(corban?.document || "")}
                </Text>
                {corban?.id && (
                  <Text fontSize="sm" color="gray.400">
                    ID {corban.id}
                  </Text>
                )}
              </HStack>
            </VStack>
          </HStack>

          <HStack spacing={3}>
            {splitStatus && (
              <Badge
                colorScheme={statusScheme}
                borderRadius="full"
                px={3}
                py={1.5}
                fontSize="11px"
                textTransform="none"
                whiteSpace="normal"
                textAlign="center"
              >
                {SplitStatusLabels[splitStatus]}
              </Badge>
            )}
            {corban?.id && (
              <Button
                onClick={handleEditCorban}
                leftIcon={<EditIcon />}
                colorScheme="blue"
                size="sm"
                variant="outline"
                flexShrink={0}
              >
                Editar Corban
              </Button>
            )}
          </HStack>
        </Flex>
      </Box>

      <SectionCard title="Dados do Corban" icon={FaBuilding} accent="blue">
        <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={5}>
          <InfoField
            label="CNPJ do Corban"
            value={documentFormatter(corban?.document || "")}
          />
          <InfoField label="Razão Social" value={corban?.legalName} />
          <InfoField label="Nome Fantasia" value={corban?.businessName} />
          <InfoField
            label="Responsável pela Operação"
            value={promoterMasterName}
          />
        </SimpleGrid>
      </SectionCard>

      {masterUser && (
        <SectionCard
          title="Usuário master desse Corban"
          icon={FaUserTie}
          accent="purple"
        >
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={5}>
            <InfoField
              label="Nome do responsável"
              value={masterUser.masterUserName}
            />
            <InfoField
              label="Sobrenome do responsável"
              value={masterUser.masterUserLastName}
            />
            <InfoField
              label="CPF"
              value={cpfFormatter(masterUser.masterUserCpf)}
            />
            <InfoField
              label="Telefone"
              value={formatPhone(masterUser.masterUserPhone)}
            />
            <InfoField label="Email" value={masterUser.masterUserEmail} />
          </SimpleGrid>
        </SectionCard>
      )}

      <SectionCard
        title="Informações Bancárias"
        icon={FaUniversity}
        accent="teal"
      >
        <VStack align="stretch" spacing={5}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={5}>
            <InfoField
              label="Email de Comissão"
              value={financialSettings?.commissionEmail}
            />
            <InfoField
              label="Modelo de Repasse Comissão"
              value={getCommissionModeDescription()}
              highlight
            />
            <InfoField label="Retenção de Impostos">
              <Badge
                colorScheme={financialSettings?.taxWithholding ? "orange" : "gray"}
                borderRadius="full"
                px={3}
                py={1}
                fontSize="xs"
              >
                {financialSettings?.taxWithholding ? "Sim" : "Não"}
              </Badge>
            </InfoField>
          </SimpleGrid>

          {financialSettings?.taxWithholding && taxes.length > 0 && (
            <Box
              borderRadius="lg"
              bg="orange.50"
              borderWidth="1px"
              borderColor="orange.100"
              px={4}
              py={4}
            >
              <Text
                fontSize="11px"
                fontWeight="semibold"
                textTransform="uppercase"
                letterSpacing="0.06em"
                color="orange.700"
                mb={3}
              >
                Impostos retidos
              </Text>
              <SimpleGrid columns={{ base: 2, md: 4 }} spacingX={6} spacingY={4}>
                {taxes.map((taxSetting, index) => (
                  <VStack key={index} align="start" spacing={0}>
                    <Text fontSize="lg" fontWeight="bold" color="orange.600">
                      {taxSetting.Tax?.percentage}%
                    </Text>
                    <Text fontSize="xs" color="gray.600">
                      {taxSetting.Tax?.name}
                    </Text>
                  </VStack>
                ))}
              </SimpleGrid>
            </Box>
          )}

          <Divider borderColor="gray.100" />

          <VStack align="stretch" spacing={4}>
            <InfoField label="Modo de Pagamento">
              <Badge
                colorScheme={isBankAccount ? "blue" : "green"}
                borderRadius="full"
                px={3}
                py={1}
                fontSize="xs"
              >
                {isBankAccount ? "Conta Bancária" : "PIX"}
              </Badge>
            </InfoField>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacingX={6} spacingY={5}>
              {isBankAccount ? (
                <>
                  <InfoField label="Banco" value={financialSettings?.bank} />
                  <InfoField
                    label="Agência Bancária"
                    value={financialSettings?.agency}
                  />
                  <InfoField
                    label="Conta Bancária"
                    value={financialSettings?.bankAccount}
                  />
                  <InfoField
                    label="Dígito"
                    value={financialSettings?.accountDigit}
                  />
                </>
              ) : (
                <>
                  <InfoField
                    label="Pix através de"
                    value={
                      FinancialSettingsPixKeyTypeLabels[
                      financialSettings?.pixKeyType as FinancialSettingsPixKeyType
                      ]
                    }
                  />
                  <InfoField
                    label="Chave Pix"
                    value={financialSettings?.pixKey}
                  />
                </>
              )}
            </SimpleGrid>
          </VStack>
        </VStack>
      </SectionCard>

      {objectTimeline && (
        <Tabs variant="soft-rounded" colorScheme="green" size="sm" isLazy>
          <SectionCard
            title="Timeline de Aprovação"
            icon={FaHistory}
            accent="green"
            action={
              <TabList border="none" flexShrink={0}>
                <Tab fontSize="xs">Processo atual</Tab>
                <Tab fontSize="xs">Histórico</Tab>
              </TabList>
            }
          >
            <TabPanels>
              <TabPanel px={0} py={0}>
                <SplitTimeline timeline={objectTimeline} mode="current" />
              </TabPanel>
              <TabPanel px={0} py={0}>
                <SplitTimeline timeline={objectTimeline} mode="history" />
              </TabPanel>
            </TabPanels>
          </SectionCard>
        </Tabs>
      )}
    </VStack>
  );
}
